import { Skeleton } from '@tiger/bs-comps';
import { useMount } from '@tiger/hooks';
import lodash from 'lodash';
import { observer } from 'mobx-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useSearchParams } from 'react-router-dom';
import Footer from 'components/DisclosureFooter';
import Progress from 'components/OpenProgress';
import RouteLayout from 'components/RouteLayout';
import { useLogo } from 'modules/Root/Logo';
import security from 'stores/security';
import values from 'stores/values';
import { CategoryEnum, CustomerProcessStatusEnum, IdTypeEnum } from 'types/customer';
import { HIDE_IRA } from 'utils/constants';
import { getOpenCacheKey } from 'utils/helper';
import Account, { AccountFormutil } from './components/Account';
import Category, { CategoryFormutil } from './components/Category';
import { getAccountParamsByQuery, getDefaultUploadPath } from './helper';
import { useCustomerInfoFromBridge } from './hook';
import { getNonSSNRoutes } from './NonSSN';
import { getSSNRoutes } from './SSN';

const Open: React.FC = () => {
    const location = useLocation();
    const { isLogin, currentProcessStatus, customerInfo, processData } = security;
    const processMissingData = processData?.missing;
    // customerInfo这时候不一定存在
    const { customer_type: infoCustomerType, id_type: customerIdType } = customerInfo || {};
    const [isInitializing, setIsInitializing] = useState(true);
    const [searchParams] = useSearchParams();
    const queryCategory = searchParams.get('category');
    const queryCustomerType = searchParams.get('customerType');
    // 记录已经是否初始化了
    const isInitializedRef = useRef(false);
    // 从bridge中读取【针对TUPSDK用户】
    const { data: dataBridgeCustomer, isLoading: isLoadingBridgeCustomer } = useCustomerInfoFromBridge();
    // 从缓存中读取对应的数据, 可以判断开户流程中的一些状态
    const {
        category: storageCategory,
        temporaryOpenType: storageTempOpenType,
        customer_type: storageCustomerType,
        id_type: storageIdType
    } = values.getParams<(AccountFormutil & CategoryFormutil) | undefined>() || {};
    // customerType可能为0, 这里不能使用||, 而应该使用??
    const currentCustomerType = storageCustomerType ?? infoCustomerType;
    const currentIdType = customerIdType || storageIdType;

    const commonBaseRoute = useMemo(
        () => (
            <>
                {currentProcessStatus === CustomerProcessStatusEnum.UNACCOUNT && (
                    <>
                        <Route path="category" element={<Category />} />
                        <Route path="account" element={<Account />} />
                    </>
                )}
            </>
        ),
        [currentProcessStatus]
    );
    const commonRoutes = useMemo(() => {
        let routes = <></>;

        if (!isInitializing) {
            const defaultUploadPath = getDefaultUploadPath(processMissingData);

            if (currentIdType === IdTypeEnum.SSN_IDS) {
                routes = getSSNRoutes({
                    currentProcessStatus,
                    location,
                    openType: storageTempOpenType,
                    defaultUploadPath
                });
            } else {
                // 默认使用非SSN的路由，因为这里可能会出现没有currentIdType的情况（进入时如果没有缓存是确定不了id_type的）
                // SSN开户在Account页选择确定id_type，非SSN开户在OCR页面确定id_type
                routes = getNonSSNRoutes({
                    currentProcessStatus,
                    location,
                    openType: storageTempOpenType,
                    defaultUploadPath
                });
            }
        }

        return routes;
    }, [currentIdType, isInitializing, currentProcessStatus, location, storageTempOpenType, processMissingData]);

    useMount(() => {
        const initPage = () => {
            setIsInitializing(true);

            const CACHE_KEY = getOpenCacheKey();

            let cacheParams: any = {};

            // 页面加载后读取本地缓存的数据
            try {
                const sessionData = sessionStorage.getItem(CACHE_KEY);

                if (sessionData) {
                    cacheParams = JSON.parse(sessionData);
                }
            } catch {}

            const curCacheAccountParams = cacheParams.account;
            const curCacheCategoryParams = cacheParams.category;

            /**
             * @tag HIDE_IRA
             * 鉴于ira验收周期过长, 所以临时隐藏ira, 方便后续代码开发合并, 验收完毕打开的时候需要重新回测
             */
            // 只有在未开户且没有缓存的账户信息时, 才去初始化开户方式/账户信息
            if (!HIDE_IRA && currentProcessStatus === 'UNACCOUNT') {
                const { account, category } = getAccountParamsByQuery(queryCategory, queryCustomerType);

                if (lodash.isEmpty(curCacheAccountParams) && lodash.isEmpty(curCacheCategoryParams)) {
                    cacheParams.account = account;
                    cacheParams.category = category;
                } else {
                    /**
                     * 如果缓存中有账户信息数据, 那么当缓存中的账户信息跟链接上的不一致的时候, 还需要处理一种特殊情况:
                     * 比如用户在Individual流程中, 但是在中途返回到个人中心, 又继续ira的开户流程, 这时候需要清除缓存信息,然后重新初始化
                     * 重新初始化之后需要根据链接上的category和customerType来初始化账户信息
                     */
                    if (
                        (queryCategory && curCacheCategoryParams.category !== queryCategory) ||
                        // 链接上的customerType是string类型, 这里必须要判断是否存在, 因为customerType为0也有具体的含义
                        (queryCustomerType && curCacheAccountParams.customer_type !== Number(queryCustomerType))
                    ) {
                        cacheParams = {
                            account,
                            category
                        };
                    }

                    if (dataBridgeCustomer) {
                        // TODO: CHECK
                        cacheParams.baseinfo = {
                            ...dataBridgeCustomer,
                            ...cacheParams.personalInfo
                        };
                    }
                }
            }

            // 当前开户流程组件挂载时初始化
            values.init(3, cacheParams, CACHE_KEY);
            setIsInitializing(false);
            isInitializedRef.current = true;
        };

        initPage();
    });

    useLogo({ customerType: currentCustomerType });

    useEffect(() => {
        // 如果数据已经初始化了，则需要重新设置，否则在初始化流程中设置即可
        if (isInitializedRef.current) {
            // TODO: 需要check数据字段存储地方
            const cachePersonalInfo = values.getParams('baseinfo');

            values.mergeParams('baseinfo', {
                ...dataBridgeCustomer,
                ...cachePersonalInfo
            });
        }
    }, [dataBridgeCustomer]);

    useEffect(() => {
        // 个人户一共3步
        if (storageCategory === CategoryEnum.INDIVIDUAL) {
            values.setTotalStep(3);
        } else {
            // IRA一共4步
            values.setTotalStep(4);
        }
    }, [storageCategory]);

    if (!isLogin) {
        // 目前线上逻辑：未登录条件下访问/open或者未知路由都会被定向到/signup
        return <Navigate to={{ ...location, pathname: '/signup' }} replace />;
    }

    if (isInitializing || isLoadingBridgeCustomer) {
        return <Skeleton line={5} />;
    }

    return (
        <div>
            <Progress type="fraction" />
            <Routes>
                <Route element={<RouteLayout />}>
                    {commonBaseRoute}
                    {commonRoutes}
                </Route>
                <Route path=":customerId/*" element={<RouteLayout />}>
                    {commonBaseRoute}
                    {commonRoutes}
                </Route>
            </Routes>
            <Footer />
        </div>
    );
};

export default observer(Open);
export * from './type';
